import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { postNamedQuery, postNamedCommand } from '../../../../../service'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import SydLabel, { hookFormFieldErrorAdapter } from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydCheckbox from '../../../../commonDesign/SydCheckbox'
import SydAutocomplete from '../../../../commonDesign/SydAutocomplete'
import SydButton from '../../../../commonDesign/Button'
import SydModal from '../../../../commonDesign/SydModal'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import WYSIWYGEditor from '../../../WYSIWYGEditor'
import { membersToOptions, renderContactOption, editorBorderStyle } from '../shared/redtailUIHelpers'
import useWorkflowTemplates from '../shared/useWorkflowTemplates'
import useCategoryOptions from './useCategoryOptions'

const DEBOUNCE_MS = 350

function decodeEntityEncodedHtml (str) {
  if (!str || typeof str !== 'string') return str
  const doc = new DOMParser().parseFromString(str, 'text/html')
  return doc.body.textContent || str
}

const CREATE_DEFAULTS = {
  workflow_template_id: '',
  name: '',
  contact_id: null,
  category_id: '',
  due_date: '',
  exclude_weekends: false,
  description: ''
}

function CreateWorkflowModal ({ open, onClose, config, members }) {
  const { clientId } = useAppContext()
  const queryClient = useQueryClient()
  const { options: templateOptions, getTemplate } = useWorkflowTemplates(clientId, open)
  const categoryOptions = useCategoryOptions(clientId, config?.categoryIds)

  const isUserScoped = config?.scope === 'user'
  const familyOptions = useMemo(() => membersToOptions(members), [members])

  const [searchOptions, setSearchOptions] = useState([])
  const [contactLoading, setContactLoading] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const debounceRef = useRef(null)
  const prevTemplateNameRef = useRef('')

  useEffect(() => () => clearTimeout(debounceRef.current), [])

  const contactOptions = isUserScoped ? searchOptions : familyOptions

  const allTemplateOptions = useMemo(() => [
    { label: '(No Template)', value: '' },
    ...templateOptions
  ], [templateOptions])

  const allCategoryOptions = useMemo(() => [
    { label: '(None)', value: '' },
    ...categoryOptions
  ], [categoryOptions])

  const { control, handleSubmit, setValue, getValues, reset } = useForm({
    defaultValues: CREATE_DEFAULTS
  })

  useEffect(() => {
    if (open) {
      reset(CREATE_DEFAULTS)
      setSubmitError(null)
      prevTemplateNameRef.current = ''
    }
  }, [open, reset])

  const searchContacts = useCallback((term) => {
    if (!isUserScoped) return
    if (!term || term.length < 2) {
      setSearchOptions([])
      return
    }
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setContactLoading(true)
      try {
        const { data } = await postNamedQuery('coreData', 'execute-redtail-domain-query', {
          query: 'searchContactsBasic',
          autoResolveConnection: true,
          clientId,
          request: { params: { name: term } }
        })
        const contacts = data?.contacts ?? []
        setSearchOptions(contacts.map(c => ({
          label: c.full_name || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || `Contact ${c.id}`,
          value: c.id
        })))
      } catch {
        setSearchOptions([])
      } finally {
        setContactLoading(false)
      }
    }, DEBOUNCE_MS)
  }, [clientId, isUserScoped])

  const handleTemplateChange = useCallback((templateId) => {
    setValue('workflow_template_id', templateId || '')

    if (!templateId) {
      const currentName = getValues('name')
      if (currentName === prevTemplateNameRef.current) {
        setValue('name', '')
      }
      setValue('description', '')
      setValue('category_id', '')
      setValue('exclude_weekends', false)
      prevTemplateNameRef.current = ''
      return
    }

    const tpl = getTemplate(templateId)
    if (!tpl) return

    const currentName = getValues('name')
    if (!currentName || currentName === prevTemplateNameRef.current) {
      setValue('name', tpl.name || '')
    }
    prevTemplateNameRef.current = tpl.name || ''

    setValue('description', tpl.description ? decodeEntityEncodedHtml(tpl.description) : '')
    setValue('category_id', tpl.category_id || '')
    setValue('exclude_weekends', tpl.exclude_weekends != null ? !!tpl.exclude_weekends : false)
  }, [getTemplate, getValues, setValue])

  const { mutateAsync: createWorkflow, isLoading: isSaving } = useMutation({
    mutationFn: (body) => postNamedCommand('coreData', 'execute-redtail-domain-command', {
      command: 'createWorkflow',
      autoResolveConnection: true,
      clientId,
      request: { body }
    }),
    onSuccess: () => {
      onClose()
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.redtailDomainQuery, 'list-preset']
      })
    }
  })

  const onFormSubmit = useCallback(async (values) => {
    setSubmitError(null)
    try {
      const body = {
        workflow_template_id: values.workflow_template_id || undefined,
        name: values.name || undefined,
        due_date: values.due_date || undefined,
        description: values.description || undefined,
        category_id: values.category_id ? Number(values.category_id) : undefined,
        exclude_weekends: values.exclude_weekends != null ? !!values.exclude_weekends : undefined
      }
      if (values.contact_id) {
        body.contact_id = Number(values.contact_id)
      }
      await createWorkflow(body)
    } catch (err) {
      setSubmitError(err?.message || 'Failed to create workflow')
    }
  }, [createWorkflow])

  return (
    <SydModal
      open={open}
      onClose={onClose}
      title='Create Workflow'
      size='xmedium'
      disablePortal={false}
    >
      <form onSubmit={handleSubmit(onFormSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px 0' }}>
        <Controller
          name='workflow_template_id'
          control={control}
          rules={{ required: 'Template is required' }}
          render={({ field, fieldState }) => (
            <SydLabel label='Workflow Template' required error={hookFormFieldErrorAdapter(fieldState)}>
              <SydSelect
                options={allTemplateOptions}
                value={field.value || ''}
                onChange={(e) => handleTemplateChange(e.target.value || '')}
              />
            </SydLabel>
          )}
        />

        <Controller
          name='name'
          control={control}
          rules={{ required: 'Name is required' }}
          render={({ field, fieldState }) => (
            <SydLabel
              label='Workflow Name'
              required
              error={hookFormFieldErrorAdapter(fieldState)}
            >
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='Workflow name'
              />
            </SydLabel>
          )}
        />

        <Controller
          name='contact_id'
          control={control}
          render={({ field }) => (
            <SydLabel label='Contact'>
              <SydAutocomplete
                options={contactOptions}
                value={contactOptions.find(o => o.value === field.value) ?? null}
                onChange={(_e, opt) => field.onChange(opt?.value ?? null)}
                onInputChange={isUserScoped ? (_e, val) => searchContacts(val) : undefined}
                isLoading={contactLoading}
                renderOption={(option) => renderContactOption(option)}
                placeholder={isUserScoped ? 'Search contacts...' : 'Select a contact...'}
              />
            </SydLabel>
          )}
        />

        <Controller
          name='category_id'
          control={control}
          render={({ field }) => (
            <SydLabel label='Category'>
              <SydSelect
                options={allCategoryOptions}
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value || '')}
              />
            </SydLabel>
          )}
        />

        <Controller
          name='due_date'
          control={control}
          render={({ field }) => (
            <SydLabel label='Target Date'>
              <SydInput
                type='date'
                {...field}
                value={field.value || ''}
              />
            </SydLabel>
          )}
        />

        <Controller
          name='exclude_weekends'
          control={control}
          render={({ field }) => (
            <SydCheckbox
              label='Exclude Weekends'
              checked={!!field.value}
              onChange={(e) => field.onChange(e.target.checked)}
            />
          )}
        />

        <Controller
          name='description'
          control={control}
          render={({ field }) => (
            <SydLabel label='Description'>
              <div style={editorBorderStyle}>
                <WYSIWYGEditor
                  isControlled
                  value={field.value || ''}
                  onChange={field.onChange}
                  placeholder='Description...'
                  minHeight={100}
                />
              </div>
            </SydLabel>
          )}
        />

        {submitError ? (
          <div style={{ color: 'var(--color-decline-500)', fontSize: 'var(--font-size-sm)' }}>
            {submitError}
          </div>
        ) : null}

        <SydModalActions>
          <SydButton
            variant='outline'
            size='sm'
            onClick={onClose}
            type='button'
          >
            Cancel
          </SydButton>
          <SydButton
            variant='primary'
            size='sm'
            type='submit'
            processing={isSaving}
          >
            Create Workflow
          </SydButton>
        </SydModalActions>
      </form>
    </SydModal>
  )
}

CreateWorkflowModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  config: PropTypes.object,
  members: PropTypes.array
}

export default CreateWorkflowModal
